import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { AlertService } from "../services/alert.service";
import { Globals } from "../services/globals";
import { ClientLoginStage, UserType } from "../enum";
import { AuthPayload } from "../engine/models/models";
import { LoginService } from "./services/login.service";
import { AuthenticationService } from "../services/authenticate.service";
import { BehaviorSubject } from "rxjs";
import { environment } from "../../environments/environment.prod";
import { CollectionsService } from "../services/collection.service";
import Swal from "sweetalert2";

@Component({
  templateUrl: "login.component.html",
  styleUrls: ["login.component.css", "utils.css"]
})

export class LoginComponent implements OnInit {
  userData = new BehaviorSubject<AuthPayload>(new AuthPayload());
  loginForm: UntypedFormGroup;
  loading: boolean;
  errorMessage: string;
  networkError = false;
  isError = false;
  isLoading = false;
  submitted = false;
  clientLoginModal = new AuthPayload();
  isAdminLogin = false;
  emailAddress: string;
  contractNumber: string;
  passwordError: string;
  sso: boolean;
  isProd: boolean;

  get f() {
    return this.loginForm.controls;
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private loginService: LoginService,
    private alertService: AlertService,
    private collectionsService: CollectionsService,
    private globals: Globals) {
    this.globals.hideHeader = true;

    let userEmail = localStorage.getItem("userEmail");
    sessionStorage.clear();
    localStorage.clear();
    this.isProd = environment.production;
    localStorage.setItem("userEmail", userEmail);
  }

  disableInputs() {
    if (this.loginForm) {
      this.loginForm.get('email').disable();
      if (this.loginForm.get('password')) {
        this.loginForm.get('password').disable();
      }
      if (this.loginForm.get('contractId')) {
        this.loginForm.get('contractId').disable();
      }
    }
  }

  enableInputs() {
    if (this.loginForm) {
      this.loginForm.get('email').enable();
      if (this.loginForm.get('password')) {
        this.loginForm.get('password').enable();
      }
      if (this.loginForm.get('contractId')) {
        this.loginForm.get('contractId').enable();
      }
    }
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {

      localStorage.clear();
      localStorage.setItem("userEmail", params.email);
      const email = params.email;
      const contractId = params.contractId;
      if (email && contractId) {
        this.sso = true;
        this.isLoading = true;
        this.clientLoginModal.email = email;
        this.clientLoginModal.contractId = contractId;
        this.authenticate();
      } else {
        this.intializeClientform("", "");
      }
    });
    this.globals.hideHeader = true;
  }

  intializeClientform(defaultUser: string, defaultContractId: string) {
    this.loginForm = this.formBuilder.group({
      contractId: [defaultContractId, Validators.required],
      email: [defaultUser, [Validators.required, Validators.email]],
    });
  }

  intializeAdminform() {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  setUserDetails() {
    if (localStorage.getItem('authToken')) {
      const userDetails = new AuthPayload();
      const decodeUserDetails = JSON.parse(window.atob(localStorage.getItem('authToken').split('.')[1]));
      userDetails.id = decodeUserDetails.jti;
      userDetails.isLoggedIn = true;
      userDetails.role = decodeUserDetails.role;
      userDetails.email = decodeUserDetails.email;
      this.userData.next(userDetails);
    }
  }

  onSubmit() {
    this.errorMessage = "";
    this.submitted = true;
    if (this.loginForm.invalid) {
      if (this.isAdminLogin) {
        if (this.loginForm.controls.email.value !== "" && this.loginForm.controls.password.value === "") {
          this.passwordError = "Please enter password.";
          return;
        }
        if (this.loginForm.controls.email.value === "" && this.loginForm.controls.password.value === "") {
          this.emailAddress = "Please enter email address.";
          this.passwordError = "Please enter password.";
          return;
        }
        if (this.loginForm.controls.password.value !== "" && this.loginForm.controls?.password?.errors?.minlength) {
          this.passwordError = "password must be at least 8 characters.";
          return;
        }

        if (this.loginForm.controls.email.value === "" && this.loginForm.controls.password.value !== "") {
          this.passwordError = "Please enter email address.";
          return;
        }
        if (this.loginForm.controls.email.value !== "" && this.loginForm.controls.email.invalid) {
          this.emailAddress = "Please enter valid email address.";
          return;
        }
      }
      else {
        if (this.loginForm.controls.email.value === "") {
          this.emailAddress = "Please enter email address.";
          return;
        }

        if (this.loginForm.controls.email.value !== "" && this.loginForm.controls.email.invalid) {
          this.emailAddress = "Please enter valid email address.";
          return;
        }

        if (this.loginForm.controls.contractId.value === "" || !this.loginForm.controls.contractId.value) {
          return;
        }
      }
    }
    this.alertService.clear();

    this.loading = true;
    this.networkError = false;

    this.disableInputs();

    // Gather all parameters
    if (this.isAdminLogin) {
      this.clientLoginModal.password = this.loginForm.controls["password"].value;
      this.clientLoginModal.contractId = null;
    } else {
      this.clientLoginModal.password = null;
      this.clientLoginModal.contractId = this.loginForm.controls["contractId"].value;
    }
    this.clientLoginModal.email = this.loginForm.controls["email"].value;
    localStorage.setItem("userEmail", this.loginForm.controls["email"].value);
    this.authenticate();
  }

  authenticate() {
    this.loginService.login(this.clientLoginModal).subscribe(response => {
      if (response.isSuccessful) {
        if (this.isAdminLogin) {
          this.globals.engine = null;
          this.loading = false;
          this.enableInputs();
          localStorage.setItem("appointment", JSON.stringify(response));
          localStorage.setItem("userType", JSON.stringify({ "isAdmin": true }));
          localStorage.setItem("userName", JSON.stringify(this.clientLoginModal.email));
          this.router.navigate(['/gallery']);
        }
        else {
          this.loginSuccess(response);
        }

        localStorage.setItem('authToken', response.token);
        this.setUserDetails();
      }
      else {        
        this.isError = true;
        this.loading = this.networkError = false;
        this.enableInputs();
        if (this.sso) {
          this.isLoading = false;
        }
        if (this.isAdminLogin) {
          this.errorMessage = "The Email/Password Entered was not found.";
        }
        else if (response.showPopup) {
          if (this.sso) {
            this.isError = false;
          }
          Swal.fire({
            title: 'Important Information For You:',
            text: response.message,
            confirmButtonText: 'Close',
            customClass: { popup: 'custome-box' }
          })
        }
        else {
          if (response.message) {
            this.isError = false;
            if (this.sso) {
              this.isError = true;
            }            
            this.networkError = true;
            this.errorMessage = response.message;
          } else {
            this.errorMessage = "The Email/Contract ID Entered was not found.";
          }
        }
      }
    }, error => {
      this.errorMessage = 'An issue occurred while attempting to contact the service.';
      this.submitted = this.loading = false;
      this.enableInputs()
      this.networkError = true;
    });
  }

  loginSuccess(response: AuthPayload) {
    response.appointment.loginBy = UserType.Client;
    localStorage.setItem("userName", JSON.stringify(response.appointment.clientName));

    //Use Co-Client Name if login by co-client
    if (response.appointment.coClientEmail && response.appointment.coClientEmail === this.clientLoginModal?.email) {
      response.appointment.loginBy = UserType.CoClient;
      localStorage.setItem("userName", JSON.stringify(response.appointment.coClientName));
    }

    this.globals.appointment = response.appointment;
    this.globals.isImpersonating = response.isImpersonating;
    this.globals.isReadOnly = response.isReadOnly;
    localStorage.setItem("userType", JSON.stringify({ "isAdmin": false }));
    this.globals.getCollectionThemes();
    this.redirectToPageForClient(response);
  }

  redirectToPageForClient(data) {
    this.enableInputs();
    this.globals.engine = null;

    if (data.appointment.stage === ClientLoginStage.Welcome) {
      this.router.navigate(['/welcome']);
    }
    else if (data.appointment.stage === ClientLoginStage.InspirationOverview) {
      this.router.navigate(['/inspiration']);
    }
    else if (data.appointment.stage === ClientLoginStage.Selection) {
      this.router.navigate(['/dashboard']);
    }
    else if (data.appointment.stage === ClientLoginStage.Finalize) {
      this.router.navigate(['/review']);
    }
  }

  formReset() {
    this.submitted = false;
    this.loginForm.reset();
  }

  login() {
    this.formReset();
    this.networkError = false;
    this.isAdminLogin = !this.isAdminLogin;
    if (this.isAdminLogin) {
      this.intializeAdminform();
    }
    else {
      this.intializeClientform("", "");
    }
    this.errorMessage = "";
  }

  validate() {
    if (this.submitted) {
      if (this.loginForm.controls.email.value === "") {
        this.emailAddress = "Please enter email address.";
        return;
      }
      if (this.loginForm.controls.email.status != 'VALID') {
        this.emailAddress = "Please enter Valid Email.";
        return;
      }
      if (this.isAdminLogin) {
        if (this.loginForm.controls.password.value !== "" && this.loginForm.controls?.password?.errors?.minlength) {
          this.passwordError = "password must be at least 8 characters.";
          return;
        }
      }
    }
  }
}
